/**
 * MaskZoneEditor — éditeur visuel des 4 points QUAD d'un template mockup.
 *
 * Affiche l'image du template avec 4 poignées draggables aux coins du
 * quadrilatère (TL → TR → BR → BL en sens horaire). Le BE consomme ces
 * 4 points en pixels image source ; ce composant convertit display↔image
 * via le ratio de rendu.
 *
 * Usage :
 *   <MaskZoneEditor
 *     imageUrl={preview}
 *     imageWidth={tpl.width}
 *     imageHeight={tpl.height}
 *     value={maskZone}
 *     onChange={setMaskZone}
 *   />
 *
 * Le composant est non-contrôlé sur les drags en cours (état local),
 * `onChange` est appelé à chaque déplacement (utiliser un debounce externe
 * si tu veux limiter les writes au form RHF).
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import type { MaskZone, Point2D } from '@/types/mockup-templates'

import './MaskZoneEditor.css'

interface MaskZoneEditorProps {
  imageUrl: string
  imageWidth: number
  imageHeight: number
  value: MaskZone
  onChange: (zone: MaskZone) => void
  disabled?: boolean
  /**
   * Lecture seule : affiche le polygone sans handles ni interactions.
   * Utilisé pour visualiser la zone auto-extraite d'un PSD avant que
   * l'utilisatrice décide d'ajuster manuellement.
   */
  readOnly?: boolean
}

const HANDLE_LABELS = ['Coin haut-gauche', 'Coin haut-droit', 'Coin bas-droit', 'Coin bas-gauche']

// Loupe de zoom — affichée pendant le drag pour donner une précision
// pixel-perfect là où l'image affichée est typiquement 3-4× plus petite
// que l'image source.
const LOUPE_SIZE_PX = 200
const LOUPE_ZOOM = 3

export default function MaskZoneEditor({
  imageUrl,
  imageWidth,
  imageHeight,
  value,
  onChange,
  disabled = false,
  readOnly = false,
}: MaskZoneEditorProps) {
  const interactive = !disabled && !readOnly
  const containerRef = useRef<HTMLDivElement>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  // Track display size of the container to compute the image↔display ratio.
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const updateSize = () => {
      const rect = el.getBoundingClientRect()
      setContainerSize({ width: rect.width, height: rect.height })
    }
    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const ratio = containerSize.width / imageWidth || 1

  const handleMouseDown = (index: number) => (e: React.PointerEvent) => {
    if (!interactive) return
    e.preventDefault()
    setDraggingIndex(index)
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (draggingIndex === null) return
      const el = containerRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      // Position relative à l'image dans l'espace de l'image (pas display)
      const xDisplay = e.clientX - rect.left
      const yDisplay = e.clientY - rect.top
      const xImage = Math.max(0, Math.min(imageWidth, Math.round(xDisplay / ratio)))
      const yImage = Math.max(0, Math.min(imageHeight, Math.round(yDisplay / ratio)))
      const next: MaskZone = [...value] as MaskZone
      next[draggingIndex] = [xImage, yImage] as Point2D
      onChange(next)
    },
    [draggingIndex, imageHeight, imageWidth, onChange, ratio, value],
  )

  const handlePointerUp = useCallback(() => {
    setDraggingIndex(null)
  }, [])

  // Construit les coordonnées des 4 poignées en display px.
  const displayPoints = value.map(([x, y]) => ({
    x: x * ratio,
    y: y * ratio,
  }))

  // Polygon stroke en SVG overlay
  const polygonPoints = displayPoints.map((pt) => `${pt.x},${pt.y}`).join(' ')

  // Loupe : positionnée dans le coin opposé au handle dragué pour ne pas
  // se faire couvrir par le curseur. Évalué seulement quand un drag est
  // actif (sinon `null`).
  const loupePosition: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | null =
    draggingIndex !== null && containerSize.width > 0
      ? (() => {
          const pt = displayPoints[draggingIndex]
          const isLeft = pt.x < containerSize.width / 2
          const isTop = pt.y < containerSize.height / 2
          // Loupe diagonalement opposée au handle
          if (isTop && isLeft) return 'bottom-right'
          if (isTop && !isLeft) return 'bottom-left'
          if (!isTop && isLeft) return 'top-right'
          return 'top-left'
        })()
      : null

  return (
    <div className="mask-zone-editor">
      <div
        ref={containerRef}
        className="mask-zone-canvas"
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        <img src={imageUrl} alt="" className="mask-zone-image" draggable={false} />
        <svg
          className="mask-zone-overlay"
          viewBox={`0 0 ${containerSize.width} ${containerSize.height}`}
          preserveAspectRatio="none"
        >
          <polygon
            points={polygonPoints}
            className="mask-zone-polygon"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {!readOnly &&
          displayPoints.map((pt, i) => (
            <div
              key={i}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label={HANDLE_LABELS[i]}
              aria-disabled={disabled || undefined}
              className="mask-zone-handle"
              data-active={draggingIndex === i || undefined}
              data-disabled={disabled || undefined}
              style={{ left: `${pt.x}px`, top: `${pt.y}px` }}
              onPointerDown={handleMouseDown(i)}
            >
              <span className="mask-zone-handle-label">{i + 1}</span>
            </div>
          ))}
        {loupePosition && draggingIndex !== null && (
          <div
            className="mask-zone-loupe"
            data-position={loupePosition}
            style={{
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: `${containerSize.width * LOUPE_ZOOM}px ${containerSize.height * LOUPE_ZOOM}px`,
              backgroundPosition: `${-(
                displayPoints[draggingIndex].x * LOUPE_ZOOM -
                LOUPE_SIZE_PX / 2
              )}px ${-(displayPoints[draggingIndex].y * LOUPE_ZOOM - LOUPE_SIZE_PX / 2)}px`,
            }}
            aria-hidden="true"
          >
            <div className="mask-zone-loupe-crosshair" />
          </div>
        )}
      </div>
      {!readOnly && (
        <p className="mask-zone-hint">
          Glisse les 4 coins pour cadrer la zone où l'œuvre sera composée. Ordre : haut-gauche →
          haut-droit → bas-droit → bas-gauche. Une loupe zoomée s'affiche pendant le drag pour la
          précision.
        </p>
      )}
    </div>
  )
}
